import React, { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import useFormValidation from '../../../hooks/useFormValidation';
import validateCompanyForm from '../../../utils/validateCompanyForm';
import AddressForm from './AddressForm';
import CurrencyService from '../../../services/currencyService';

const INITIAL_STATE = {
  name: '',
  description: '',
  currency: 'USD',
  address: { line1: '', line2: '', suburb: '', country: '', state: '', postcode: '' }
};

const CompanyForm = ({ onSubmit, onCancel, initialData = {} }) => {
  const {
    handleSubmit,
    handleChange,
    handleAddressChange,
    values,
    setValues,
    errors,
    isSubmitting,
  } = useFormValidation(INITIAL_STATE, validateCompanyForm, onSubmit);
  
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
        setValues({
            name: initialData.name || '',
            description: initialData.description || '',
            currency: initialData.currency || 'USD',
            address: initialData.address || INITIAL_STATE.address
        });
    }
}, [initialData, setValues]);


  return (
    <form onSubmit={handleSubmit} noValidate>
      <h3 className="text-lg font-bold mb-4">{initialData.id ? 'Edit Company' : 'Create New Company'}</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
          <input
            name="name"
            type="text"
            value={values.name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${errors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'}`}
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            name="description"
            value={values.description}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${errors.description ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'}`}
            rows="3"
          />
          {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
          <select
            name="currency"
            value={values.currency}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${errors.currency ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'}`}
          >
            {CurrencyService.getActiveCurrencies().map(currency => (
              <option key={currency.code} value={currency.code}>
                {currency.flag} {currency.code} - {currency.name}
              </option>
            ))}
          </select>
          {errors.currency && <p className="text-red-500 text-xs mt-1">{errors.currency}</p>}
          <p className="text-xs text-gray-500 mt-1">This currency will be used for all financial displays for this company.</p>
        </div>
        <AddressForm 
          initialAddress={values.address} 
          onAddressChange={handleAddressChange} 
          error={errors.address} 
        />
         {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
      </div>
      <div className="flex justify-end space-x-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
        <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center justify-center" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {initialData.id ? 'Update Company' : 'Create Company'}
        </button>
      </div>
    </form>
  );
};

export default CompanyForm;
